import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Loading from "components/ui/Loading";
import { getAdminDashboardFunnel } from "store/admin/api/apiSlice";
import { getPartner } from "utils/partners";
import { ProductName } from "types/general";
import OnboardingFunnel from "../../../components/OnboardingFunnel";
import DatePicker from "../../../components/DatePicker";
import * as Styled from "../styled";
import { COLORS, DEFAULT_PARTNERS, PARTNERS } from "./constants";

const getLabel = (slug) => {
  const partner = getPartner(slug);
  let label = partner?.name;
  if (slug === "all") {
    label = "All users";
  } else if (slug === "none") {
    label = "No partner";
  }
  return label;
};

const PRODUCTS = [
  { name: ProductName.earnedWageAccess, label: "EWA" },
  { name: ProductName.taxRefundAdvance, label: "Tax" },
  { name: "any", label: "Any" },
];

const FunnelExplorer: React.FC = () => {
  const [createdAtMin, setCreatedAtMin] = useState<string | null>(null);
  const [createdAtMax, setCreatedAtMax] = useState<string | null>(null);
  const [partnersToDisplay, setPartnersToDisplay] = useState(DEFAULT_PARTNERS);
  const [selectedProductName, setSelectedProductName] = useState<
    ProductName | "any"
  >(ProductName.earnedWageAccess);
  const productEntryPoint =
    selectedProductName === "any" ? null : selectedProductName;
  const { data, isFetching } = getAdminDashboardFunnel({
    createdAtMin,
    createdAtMax,
    productEntryPoint,
  });

  if (!data) return <Loading />;

  const onChangePartnerCheckbox = (event) => {
    const newPartnersToDisplay = new Set(partnersToDisplay);

    if (event.target.checked) {
      newPartnersToDisplay.add(event.target.name);
    } else {
      newPartnersToDisplay.delete(event.target.name);
    }

    setPartnersToDisplay(newPartnersToDisplay);
  };

  const funnelSeries = PARTNERS.filter((partnerSlug) =>
    partnersToDisplay.has(partnerSlug)
  ).map((referralSlug) => ({
    dataKey: referralSlug,
    label: getLabel(referralSlug),
    color: COLORS[referralSlug],
  }));

  return (
    <Styled.Wrapper>
      <Styled.Section size={12}>
        <Styled.Options>
          <ToggleButtonGroup
            onChange={(_e, value) => {
              setSelectedProductName(value);
            }}
            value={selectedProductName}
            exclusive
          >
            {PRODUCTS.map((option) => (
              <ToggleButton value={option.name} key={option.name}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <DatePicker
            label="Start"
            defaultValue={dayjs().subtract(1, "month")}
            onChange={(value) => setCreatedAtMin(value.format("YYYY-MM-DD"))}
          />
          <DatePicker
            label="End"
            defaultValue={dayjs()}
            onChange={(value) => setCreatedAtMax(value.format("YYYY-MM-DD"))}
          />
        </Styled.Options>
      </Styled.Section>

      <Styled.Section size={12}>
        <Styled.Options>
          {PARTNERS.map((partnerSlug) => (
            <FormControlLabel
              key={partnerSlug}
              label={getLabel(partnerSlug)}
              control={
                <Checkbox
                  onChange={onChangePartnerCheckbox}
                  name={partnerSlug}
                  defaultChecked={DEFAULT_PARTNERS.has(partnerSlug)}
                />
              }
            />
          ))}
        </Styled.Options>
      </Styled.Section>

      <Styled.Section size={12}>
        {isFetching ? (
          <Loading />
        ) : (
          <OnboardingFunnel
            productEntryPoint={productEntryPoint}
            sortByKey="partnerSlug"
            series={funnelSeries}
            users={data.users}
          />
        )}
      </Styled.Section>
    </Styled.Wrapper>
  );
};

export default FunnelExplorer;
